import { AxiosRequestConfig } from 'axios';
import { isAxiosError } from 'axios';
import axios from 'axios';
import https from 'https';
import { bufferToString } from 'libs/end-to-end/src/generic';
import { errResult } from 'libs/end-to-end/src/result';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { LoggerPayload } from 'libs/end-to-end/src/isomorphic-logger';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { readAllResponseHeaders } from 'libs/end-to-end/src/http/http';
import { withSocketTimeoutRetry } from 'libs/end-to-end/src/http/with-socket-timeout-retry';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { HttpRequestError } from 'libs/end-to-end/src/types/errors/http-request-error';
import { JsonParseError } from 'libs/end-to-end/src/types/errors/json-parse-error';

export interface RawHttpRequestResult {
  ok: boolean;
  status: number;
  headers: Record<string, string>;
  buffer: ArrayBuffer;
  timeout: boolean;
}

export type HttpRequestData = string | Buffer | ArrayBuffer | null;

export interface HttpRequestBaseProps {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers: Record<string, string>;
  params?: Record<string, string>;
  data: HttpRequestData;
  logger: LoggerPayload;
  timeout?: number;
  httpsAgent?: https.Agent;
}

export const doHttpRequestRaw = withSocketTimeoutRetry(
  async (
    props: HttpRequestBaseProps,
  ): Promise<Result<RawHttpRequestResult, HttpRequestError>> => {
    const { url, method, headers, data, timeout, params, httpsAgent } = props;

    try {
      let config: AxiosRequestConfig<HttpRequestData> = {
        url,
        method,
        headers,
        responseType: 'arraybuffer',
        transitional: {
          forcedJSONParsing: false,
        },
        params,
        httpsAgent,
      };

      if (data) {
        config = {
          ...config,
          data,
        };
      }

      if (timeout !== undefined) {
        config = {
          ...config,
          timeout,
        };
      }

      const result = await axios.request<ArrayBuffer>(config);
      const ok = result.status >= 200 && result.status < 300;

      return okResult({
        ok,
        status: result.status ?? 0,
        headers: readAllResponseHeaders(result.headers),
        buffer: result.data,
        timeout: false,
      });
    } catch (err) {
      if (!isAxiosError(err)) {
        return errResult({
          errorType: 'HttpRequestFailedError',
          errorReason: err instanceof Error ? err.message : 'Unknown error',
        });
      }

      const status = err.response?.status ?? 0;
      const ok = status >= 200 && status < 300;
      const buffer = err.response?.data
        ? (err.response.data as ArrayBuffer)
        : new ArrayBuffer(0);

      return okResult({
        ok,
        status,
        headers: readAllResponseHeaders(err.response?.headers ?? {}),
        buffer,
        timeout: err.code === 'ECONNABORTED',
      });
    }
  },
);

export interface JsonHttpRequestResult<TSchema> {
  ok: boolean;
  status: number;
  headers: Record<string, string>;
  buffer: ArrayBuffer;
  data: TSchema | null;
  error: BaseError | null;
  parseError: JsonParseError | null;
  params?: Record<string, string>;
}

export const doHttpRequestJson = async <TSchema>(
  props: HttpRequestBaseProps & {
    schema: S2SafeParsable<unknown, TSchema>;
  },
): Promise<Result<JsonHttpRequestResult<TSchema>, HttpRequestError>> => {
  const { schema } = props;

  const rawResult = await doHttpRequestRaw(props);

  if (rawResult.resultType === 'err') {
    return errResult(rawResult.error);
  }

  const { ok, status, headers, buffer } = rawResult.value;

  const bufferToStringResult = bufferToString({
    buffer: Buffer.from(buffer),
  });

  let data: TSchema | null = null;
  let parseError: JsonParseError | null = null;
  let error: BaseError | null = null;
  if (bufferToStringResult.resultType === 'err') {
    console.error(
      'Error converting buffer to string:',
      bufferToStringResult.error,
    );
  }
  if (bufferToStringResult.resultType === 'ok') {
    const parseResult = jsonParse(bufferToStringResult.value, schema);
    const errorParseResult = jsonParse(bufferToStringResult.value, BaseError);

    if (errorParseResult.resultType === 'ok') {
      error = errorParseResult.value;
    }

    if (parseResult.resultType === 'ok') {
      data = parseResult.value;
    } else {
      parseError = parseResult.error;
    }
  }

  return okResult({
    ok,
    status,
    data,
    buffer,
    error,
    parseError,
    headers,
  });
};
